<template>
  <b-row>
    <b-col cols="12" xl="12">
      <transition name="slide">
        <b-card>
          <template #header>
            <h5 class="m-0">Menções</h5>
          </template>
          <b-card-body>
            <div class="filters-section">
              <b-form-group label="Pesquisar" class="search-filter">
                <b-input-group>
                  <b-input-group-prepend>
                    <b-input-group-text>
                      <i class="fas fa-search" aria-hidden="true"></i>
                    </b-input-group-text>
                  </b-input-group-prepend>
                  <b-form-input
                    v-model="filterText"
                    type="search"
                    placeholder="Digite para pesquisar"
                    @input="debounceFilter"
                    @keyup.enter="applyFilter"
                    @search="applyFilter"
                  />
                  <b-input-group-append>
                    <b-button :disabled="!filterText" @click="clearFilter"
                      >Limpar</b-button
                    >
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
              <b-form-group
                v-if="isRootUser"
                label="Empresa"
                class="company-filter"
              >
                <searchable-select
                  v-model="selectedCompany"
                  :options="companyOptions"
                  :placeholder="'Todas as Empresas'"
                  :search-placeholder="'Buscar empresa...'"
                  :use-company-filter="true"
                />
              </b-form-group>
            </div>

            <div class="table-container">
              <mentions-table
                :items="data.docs"
                :headers="headers"
                :loading="loading"
                @row-clicked="onRowClicked"
              />
            </div>

            <nav class="pagination-nav">
              <b-pagination
                v-model="data.page"
                :total-rows="data.total"
                :per-page="data.limit"
                prev-text="Anterior"
                next-text="Próximo"
                align="center"
              />
            </nav>
          </b-card-body>
        </b-card>
      </transition>
    </b-col>
  </b-row>
</template>

<script>
import { getMentions } from "../../api/mentions";
import { getCompanies } from "../../api/company";
import { mapGetters } from "vuex";
import protocol from "../../mixins/protocol";
import MentionsTable from "./MentionsTable.vue";
import SearchableSelect from "@/components/SearchableSelect.vue";

export default {
  name: "MentionList",
  mixins: [protocol],
  components: { MentionsTable, SearchableSelect },
  data() {
    return {
      filterText: "",
      filterDebounce: null,
      loading: false,
      selectedCompany: null,
      companies: [],
      data: {
        docs: [],
        total: 0,
        page: 1,
        pages: 0,
        limit: 15,
      },
    };
  },
  computed: {
    ...mapGetters(["profile"]),
    isRootUser() {
      return this.profile.role === "root";
    },
    companyOptions() {
      const options = [{ value: null, text: "Todas as Empresas" }];
      this.companies.forEach((company) => {
        options.push({
          value: company._id,
          text: company.fantasyName || company.name,
        });
      });
      return options;
    },
    headers() {
      return [
        {
          key: "company",
          label: "Empresa",
          thStyle: { minWidth: "200px" },
        },
        { key: "user", label: "Usuário" },
        { key: "protocol", label: "Protocolo" },
        {
          key: "createdAt",
          label: "Recebida em",
          thStyle: { width: "180px" },
        },
        { key: "status", label: "Status" },
        {
          key: "verifiedBy",
          label: "Verificado por",
          thStyle: { width: "200px" },
        },
        {
          key: "verifiedDate",
          label: "Verificada em",
          thStyle: { width: "180px" },
        },
      ];
    },
  },
  watch: {
    "data.page": "fetchMentions",
    selectedCompany() {
      this.data.page = 1;
      this.fetchMentions();
    },
  },
  async mounted() {
    if (this.isRootUser) {
      await this.fetchCompanies();
    }
    this.fetchMentions();
  },
  methods: {
    clearFilter() {
      this.filterText = "";
      this.applyFilter();
    },
    debounceFilter() {
      clearTimeout(this.filterDebounce);
      this.filterDebounce = setTimeout(() => {
        this.applyFilter();
      }, 300);
    },
    applyFilter() {
      this.data.page = 1;
      this.fetchMentions();
    },
    onRowClicked(item) {
      this.openProtocol(item.record.protocol);
    },
    async fetchCompanies() {
      try {
        const response = await getCompanies({ size: 1000, page: 1 });
        if (response && response.data && response.data.docs) {
          this.companies = response.data.docs;
        }
      } catch (error) {
        console.error("Erro ao carregar empresas:", error);
      }
    },
    async fetchMentions() {
      this.loading = true;
      try {
        const response = await getMentions({
          page: this.data.page,
          size: this.data.limit,
          filter: this.filterText,
          companyId: this.selectedCompany,
        });
        if (response && response.data) {
          this.data.docs = response.data.docs || response.data.data || [];
          this.data.total =
            response.data.total ||
            response.data.totalCount ||
            this.data.docs.length ||
            0;
          this.data.pages =
            response.data.pages || response.data.totalPages || 1;
        } else {
          this.data.docs = [];
          this.data.total = 0;
          this.data.pages = 1;
        }
      } catch (error) {
        console.error("Erro ao carregar menções:", error);
        this.$bvToast &&
          this.$bvToast.toast("Erro ao carregar menções", {
            title: "Erro",
            variant: "danger",
            solid: true,
          });
        this.data.docs = [];
        this.data.total = 0;
        this.data.pages = 1;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.table-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
  overflow: hidden;
}

.filters-section {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  align-items: flex-end;
}

.search-filter {
  flex: 2;
}

.company-filter {
  flex: 1;
  min-width: 250px;
}

::v-deep .company-filter .ss-control {
  border: 1px solid #ced4da;
  border-radius: 8px;
  padding: 0.575rem 0.75rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.pagination-nav {
  margin-top: 1.5rem;
}

::v-deep .page-item .page-link {
  border-radius: 6px;
  margin: 0 3px;
  border: 1px solid #e9ecef;
  color: #495057;
  transition: background-color 0.2s, color 0.2s;
}

::v-deep .page-item .page-link:hover {
  background-color: #f0f2f5;
  color: #0056b3;
}

::v-deep .page-item.active .page-link {
  background-color: #007bff;
  border-color: #007bff;
  color: #fff;
}

::v-deep .page-item.disabled .page-link {
  background-color: transparent;
  color: #adb5bd;
}
</style>
